import React, { useState, useRef, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import apiService from '../services/api';
import { scannerFetch } from '../utils/scannerFetch';
import './CrackerController.css';
import './DomainManager.css';
import './AtlasPageLayout.css';

interface Domain {
  id: string;
  url: string;
  addedAt: string;
}

interface CheckFile {
  name: string;
  size: number;
  modified: string;
  path: string;
  serverIdx: number;   // 0-based index in effectiveVpsList
  serverLabel: string; // "S1", "S2", …
}

interface VpsEntry { ip: string; port: string }

interface DomainManagerProps {
  domains: Domain[];
  onDomainsChange: (domains: Domain[]) => void;
  onDomainsCountChange: (count: number) => void;
  isLoading: boolean;
  licenseType?: string;
  licenseExpiry?: string;
  licenseGrabber?: boolean;
  licenseMailer?: boolean;
  licenseUsername?: string;
  vpsIp?: string;
  vpsPort?: string;
  vpsIp2?: string;
  vpsPort2?: string;
  vpsList?: VpsEntry[];
}

const SERVER_COLORS = ['#38bdf8', '#a78bfa', '#34d399', '#f59e0b', '#f87171'];

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const DomainManager: React.FC<DomainManagerProps> = ({
  domains,
  onDomainsChange,
  onDomainsCountChange,
  isLoading,
  vpsIp,
  vpsPort,
  vpsIp2,
  vpsPort2,
  vpsList,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkFiles, setCheckFiles] = useState<CheckFile[]>([]);
  const [loadingCheckFiles, setLoadingCheckFiles] = useState(false);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadServerIdx, setUploadServerIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Build unified VPS list — same logic as CrackerController
  const effectiveVpsList = useMemo<VpsEntry[]>(() => {
    let list: VpsEntry[] = [];
    if (vpsList && vpsList.length > 0) {
      list = [...vpsList];
    } else {
      if (vpsIp) list.push({ ip: vpsIp, port: vpsPort || '3007' });
    }
    if (vpsIp2 && !list.some(v => v.ip === vpsIp2)) {
      list.push({ ip: vpsIp2, port: vpsPort2 || '3007' });
    }
    return list;
  }, [vpsList, vpsIp, vpsPort, vpsIp2, vpsPort2]);

  const loadCheckFiles = async () => {
    setLoadingCheckFiles(true);
    const merged: CheckFile[] = [];

    if (effectiveVpsList.length === 0) {
      // Fallback: use apiService (primary VPS from license)
      try {
        const data = await apiService.getCheckFiles();
        const files: Omit<CheckFile, 'serverIdx' | 'serverLabel'>[] = data.files || [];
        for (const f of files) merged.push({ ...f, serverIdx: 0, serverLabel: 'S1' });
      } catch { /* ignore */ }
    } else {
      await Promise.allSettled(
        effectiveVpsList.map(async (vps, idx) => {
          try {
            const res = await scannerFetch(vps.ip, vps.port, '/check-files');
            if (!res.ok) return;
            const data = await res.json();
            const files: Omit<CheckFile, 'serverIdx' | 'serverLabel'>[] = data.files || [];
            for (const f of files) {
              merged.push({ ...f, serverIdx: idx, serverLabel: `S${idx + 1}` });
            }
          } catch { /* server offline — skip */ }
        })
      );
    }

    // Sort: server index first, then file name
    merged.sort((a, b) => a.serverIdx - b.serverIdx || a.name.localeCompare(b.name));
    setCheckFiles(merged);
    setLoadingCheckFiles(false);
  };

  const deleteFile = async (fileName: string, serverIdx: number) => {
    const vps = effectiveVpsList[serverIdx];
    const key = `${serverIdx}:${fileName}`;
    try {
      setDeletingFile(key);
      if (vps) {
        const res = await scannerFetch(
          vps.ip, vps.port,
          `/check-files/${encodeURIComponent(fileName)}`,
          { method: 'DELETE' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } else {
        await apiService.deleteCheckFile(fileName);
      }
      setSuccess(`Removed "${fileName}" from vault`);
      setCheckFiles(prev => prev.filter(f => !(f.name === fileName && f.serverIdx === serverIdx)));
    } catch {
      setError('Could not delete the file');
    } finally {
      setDeletingFile(null);
    }
  };

  const downloadFile = async (fileName: string, serverIdx: number) => {
    const vps = effectiveVpsList[serverIdx];
    const key = `${serverIdx}:${fileName}`;
    try {
      setDownloadingFile(key);
      let content = '';
      if (vps) {
        const res = await scannerFetch(
          vps.ip, vps.port,
          `/check-files/${encodeURIComponent(fileName)}/download`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        content = await res.text();
      } else {
        // fallback via apiService (triggers browser download internally)
        await apiService.downloadCheckFile(fileName);
        setSuccess(`Downloaded "${fileName}"`);
        return;
      }
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess(`Downloaded "${fileName}"`);
    } catch {
      setError('Error downloading file');
    } finally {
      setDownloadingFile(null);
    }
  };

  const uploadFile = async (file: File) => {
    if (uploadServerIdx === null) {
      setError('Please select a server before uploading');
      return;
    }
    setIsUploading(true);
    setError('');
    setSuccess('');

    try {
      const vps = effectiveVpsList[uploadServerIdx];
      const serverLabel = `Server ${uploadServerIdx + 1}`;

      if (vps) {
        const formData = new FormData();
        formData.append('file', file);
        // Send direct to the chosen VPS's upload endpoint
        const proxyUrl = `${(process.env.REACT_APP_LICENSE_API || 'https://glitchcloud.app/license-api')}/scanner-proxy/${vps.ip}/${vps.port}/domains/upload`;
        const res = await fetch(proxyUrl, { method: 'POST', body: formData });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setSuccess(`${data.totalImported || data.domains?.length || 0} targets ingested → ${serverLabel}`);
        if (data.domains && data.domains.length > 0) onDomainsChange([...domains, ...data.domains]);
        if (data.totalDomains !== undefined) onDomainsCountChange(data.totalDomains);
      } else {
        // Fallback: primary VPS via apiService
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiService.uploadDomainsFile(formData);
        setSuccess(`${response.totalImported || response.domains?.length || 0} targets ingested → ${serverLabel}`);
        if (response.domains && response.domains.length > 0) onDomainsChange([...domains, ...response.domains]);
        if (response.totalDomains !== undefined) onDomainsCountChange(response.totalDomains);
      }
      loadCheckFiles();
    } catch {
      setError('Could not import the file');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) await uploadFile(file);
  };

  useEffect(() => {
    loadCheckFiles();
  }, [effectiveVpsList.length]); // eslint-disable-line

  useEffect(() => {
    if (!error && !success) return;
    const timer = setTimeout(() => { setError(''); setSuccess(''); }, 5000);
    return () => clearTimeout(timer);
  }, [error, success]);

  const totalSize = checkFiles.reduce((acc, f) => acc + f.size, 0);
  const serverCount = effectiveVpsList.length || 1;

  return (
    <div className="ix-page lists-atlas cracker-shell cracker-shell--atlas">
      <AnimatePresence>
        {error && (
          <motion.div className="cracker-alert mb-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {error}
          </motion.div>
        )}
        {success && (
          <motion.div className="lists-atlas-toast mb-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {success}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="lists-atlas-strip">
        <div className="lists-atlas-chip">
          <span className="lists-atlas-chip-label">Vault files</span>
          <span className="lists-atlas-chip-value">{checkFiles.length}</span>
        </div>
        <div className="lists-atlas-chip">
          <span className="lists-atlas-chip-label">Stored volume</span>
          <span className="lists-atlas-chip-value">{formatFileSize(totalSize)}</span>
        </div>
        <div className="lists-atlas-chip">
          <span className="lists-atlas-chip-label">Servers</span>
          <span className="lists-atlas-chip-value">{serverCount}</span>
        </div>
      </div>

      <div className="lists-atlas-grid">
        <section
          className={`lists-atlas-drop ix-tile${dragOver ? ' lists-atlas-drop--active' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files?.[0];
            if (!file) return;
            if (!file.name.endsWith('.txt')) { setError('Only .txt corpora are accepted'); return; }
            if (uploadServerIdx === null) { setError('Select a server first'); return; }
            uploadFile(file);
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt"
            onChange={handleFileUpload}
            className="sr-only"
            disabled={isUploading || isLoading}
          />
          <div className="lists-atlas-drop-icon" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 3v12m0 0l4-4m-4 4L8 11M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h3 className="lists-atlas-drop-title">Drop corpus here</h3>
          <p className="lists-atlas-drop-desc">One target per line · duplicates skipped · large files OK</p>

          {/* ── Server picker (required before upload) ── */}
          <div className="lists-atlas-srv-pick">
            <p className="lists-atlas-srv-pick-label">Upload to server</p>
            <div className="lists-atlas-srv-pick-row">
              {effectiveVpsList.length > 0 ? effectiveVpsList.map((vps, idx) => {
                const tagColor = SERVER_COLORS[idx % SERVER_COLORS.length];
                const isSel = uploadServerIdx === idx;
                return (
                  <button
                    key={idx}
                    type="button"
                    className={`lists-atlas-srv-btn${isSel ? ' lists-atlas-srv-btn--on' : ''}`}
                    style={isSel ? { borderColor: tagColor, color: tagColor, background: `${tagColor}18` } : undefined}
                    onClick={() => setUploadServerIdx(idx)}
                  >
                    <span className="lists-atlas-srv-dot" style={{ background: isSel ? tagColor : undefined }} />
                    S{idx + 1}
                  </button>
                );
              }) : (
                <span className="lists-atlas-srv-pick-label" style={{ opacity: 0.5 }}>No server configured</span>
              )}
            </div>
          </div>

          <button
            type="button"
            className="cracker-btn cracker-btn--ghost mt-4"
            onClick={() => {
              if (uploadServerIdx === null) { setError('Select a server first'); return; }
              fileInputRef.current?.click();
            }}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading…' : 'Browse files'}
          </button>
        </section>

        <section className="lists-atlas-vault">
          <div className="lists-atlas-vault-head">
            <h3 className="lists-atlas-vault-title">Indexed corpora</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="lists-atlas-vault-count">{checkFiles.length} objects</span>
              <button
                type="button"
                className="lists-atlas-icon-btn"
                onClick={loadCheckFiles}
                disabled={loadingCheckFiles}
                aria-label="Refresh"
                title="Refresh from all servers"
              >
                ↻
              </button>
            </div>
          </div>

          {/* Server legend */}
          {serverCount > 1 && (
            <div className="lists-atlas-server-legend">
              {effectiveVpsList.map((vps, idx) => (
                <span key={idx} className="lists-atlas-server-legend-item">
                  <span
                    className="lists-atlas-server-tag"
                    style={{ background: `${SERVER_COLORS[idx % SERVER_COLORS.length]}20`, color: SERVER_COLORS[idx % SERVER_COLORS.length], borderColor: `${SERVER_COLORS[idx % SERVER_COLORS.length]}40` }}
                  >
                    S{idx + 1}
                  </span>
                  <span className="lists-atlas-server-legend-ip">{vps.ip}</span>
                </span>
              ))}
            </div>
          )}

          {loadingCheckFiles ? (
            <div className="lists-atlas-empty">
              <div className="lists-atlas-spinner" />
              <p>Reading vault…</p>
            </div>
          ) : checkFiles.length === 0 ? (
            <div className="lists-atlas-empty">
              <p className="lists-atlas-empty-title">Vault empty</p>
              <p>Ingest a .txt file — it appears here for scanner pick-up.</p>
            </div>
          ) : (
            <ul className="lists-atlas-manifest">
              {checkFiles.map((file) => {
                const fileKey = `${file.serverIdx}:${file.name}`;
                const tagColor = SERVER_COLORS[file.serverIdx % SERVER_COLORS.length];
                return (
                  <li key={fileKey} className="lists-atlas-manifest-row">
                    <div className="lists-atlas-manifest-main">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span
                          className="lists-atlas-server-tag"
                          style={{ background: `${tagColor}20`, color: tagColor, borderColor: `${tagColor}40` }}
                          title={`Server ${file.serverIdx + 1}`}
                        >
                          {file.serverLabel}
                        </span>
                        <span className="lists-atlas-manifest-name">{file.name}</span>
                      </div>
                      <span className="lists-atlas-manifest-meta">
                        {formatFileSize(file.size)} ·{' '}
                        {new Date(file.modified).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <div className="lists-atlas-manifest-actions">
                      <button
                        type="button"
                        className="lists-atlas-icon-btn"
                        onClick={() => downloadFile(file.name, file.serverIdx)}
                        disabled={downloadingFile === fileKey}
                        aria-label={`Download ${file.name}`}
                      >
                        {downloadingFile === fileKey ? '…' : '↓'}
                      </button>
                      <button
                        type="button"
                        className="lists-atlas-icon-btn lists-atlas-icon-btn--danger"
                        onClick={() => deleteFile(file.name, file.serverIdx)}
                        disabled={deletingFile === fileKey}
                        aria-label={`Delete ${file.name}`}
                      >
                        {deletingFile === fileKey ? '…' : '×'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default DomainManager;
